import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl } from "@angular/forms"
import { IQuestionComponent } from 'app/interfaces/IQuestionComponent'
import { SurveyViewerService } from '../../../services/survey-viewer.service'
import { Radio } from 'app/models/questions/Radio'
import { OpenStatementErrorMatcher } from 'app/error-matchers/OpenStatementErrorMatcher'
import {BehaviorSubject, Subject, takeUntil} from "rxjs";
import {IValidationResponse, ValidateCommentsAIService} from "@store/validateCommentsAI.service";
import {InputPersistenceService} from '../../../services/input-persistence.service';

@Component({
    selector: 'app-question-radio',
    templateUrl: './radio.component.html',
    styleUrls: ['./radio.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RadioComponent implements IQuestionComponent, OnInit, OnDestroy {

  @Input() question: Radio
  @Input() isSmallScreen: boolean
  @Input() activeLang: string

  questionFormControl: AbstractControl
  commentsFormControl: AbstractControl
  errorMatcher: OpenStatementErrorMatcher
  isValidAI: Record<string, BehaviorSubject<IValidationResponse>> = {};
  private ngUnsubscribe = new Subject<void>();

  constructor(
    public surveyViewerService: SurveyViewerService,
    private validateAi: ValidateCommentsAIService,
    private inputPersistence: InputPersistenceService
) {
  }

  ngOnInit(): void {
    this.errorMatcher = new OpenStatementErrorMatcher(this.question.isOptional)
    const rowKey = this.question.rowKeys[this.question.rowKeys.length - 1];
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).get(rowKey).get(this.question.formControlName)
    this.commentsFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).get(rowKey).get(this.question.commentsControlName)
    this.question.statementKeys.forEach(sKey => {
      const commentsControl = this.commentsFormControl.get(sKey);
      if (commentsControl) {
        this.surveyViewerService.changeFormUpdateStrategy(commentsControl, 'blur');
        // Restaurer la valeur sauvegardée si elle existe (priorité à la valeur en cours de saisie)
        const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rowKey);
        if (savedValue) {
          commentsControl.setValue(savedValue, {emitEvent: false});
        }
      }
    })

    // Surveiller les changements du Survey pour restaurer les valeurs quand le formulaire se recharge
    this.surveyViewerService.activeSurvey$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(survey => {
      if (survey) {
        this.question.statementKeys.forEach(sKey => {
          const newCommentsControl = survey.form
            .get(this.question.key)
            ?.get(rowKey)
            ?.get(this.question.commentsControlName)
            ?.get(sKey);
          
          if (newCommentsControl) {
            const currentValue = newCommentsControl.value;
            const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rowKey);
            
            if (savedValue && (!currentValue || currentValue === '')) {
              setTimeout(() => {
                newCommentsControl.setValue(savedValue, {emitEvent: false});
              }, 0);
            }
          }
        });
      }
    });
  }

  onBlur(value: string, sKey: string){
    this.commentsFormControl.get(sKey).setValue(value);
    // Note: On ne nettoie PAS ici car le formulaire peut se recharger juste après

    this.isValidAI[sKey] ??= new BehaviorSubject<IValidationResponse>({isValid: true});

    if (!this.question.disableOpenStatementsRealtimeValidation) {
      // Pour les questions à choix multiples, on passe le libellé de l'énoncé sélectionné
      const selectedStatement = this.question.statements[sKey]?.label?.[this.activeLang] || this.question.statements[sKey]?.label?.fr;

      this.validateAi.validateAnswerWithOpenAI(this.question.question[this.activeLang], value, this.activeLang, selectedStatement)
        .then((response) => {
          this.isValidAI[sKey].next(response);
          this.updateQuestionAIStatus();
        });
    }
  }

  isOptionDisabled(statementKey: string): boolean {
    const control = this.questionFormControl?.get(statementKey);
    return !!control?.disabled && !control?.value;
  }

  /**
   * Sauvegarde la valeur en temps réel (appelé à chaque frappe)
   */
  onInput(value: string, sKey: string){
    const rowKey = this.question.rowKeys[this.question.rowKeys.length - 1];
    this.inputPersistence.saveValue(this.question.key, value, sKey, rowKey);
  }

  private updateQuestionAIStatus() {
    const hasError = Object.values(this.isValidAI).some(subject => !subject.value.isValid);
    this.validateAi.updateQuestionAIValidationStatus(this.question.key, !hasError);
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
